import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBar from "@mui/material/AppBar";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import PrimaryButton from "components/PrimaryButton";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import Transition from "components/Transition";
import { DocumentWebLinkType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

type AddDocumentWebLinkDialogProps = {
  open: boolean;
  link: DocumentWebLinkType | null;
  externalId: string;
  updateLink: (url: string, name: string, original?: DocumentWebLinkType | null) => void;
  onClose: () => void;
};

export const AddDocumentWebLinkDialog: FC<AddDocumentWebLinkDialogProps> = (props) => {
  const { open, externalId, updateLink, onClose, link } = props;
  const intl = useIntl();

  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");

  const handleClose = useCallback(() => {
    setLinkUrl("");
    setLinkName("");
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open && link) {
      setLinkName(link.name);
      setLinkUrl(link.link);
    }
  }, [link, open]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="AddDocumentWebLinkDialog"
    >
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="AddDocumentWebLinkDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            {`${externalId} `} / <FormattedMessage id="file.addLink" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <StyledTextField
              value={linkName}
              label={intl.formatMessage({ id: "file.linkName" })}
              onChange={(e) => {
                setLinkName(e.target.value);
              }}
              data-testid="AddDocumentWebLinkDialog-LinkName"
            />
          </Grid>
          <Grid item>
            <StyledTextField
              value={linkUrl}
              label={intl.formatMessage({ id: "file.linkUrl" })}
              onChange={(e) => {
                setLinkUrl(e.target.value);
              }}
              data-testid="AddDocumentWebLinkDialog-LinkUrl"
            />
          </Grid>
          <Grid item>
            <PrimaryButton
              disabled={!linkName || !linkUrl}
              fullWidth
              data-testid="AddDocumentWebLinkDialog-SaveButton"
              onClick={() => updateLink(linkUrl!, linkName!, link)}
            >
              <FormattedMessage id="general.save" />
            </PrimaryButton>
          </Grid>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};
